import { LoadingOutlined, SendOutlined } from '@ant-design/icons'
import { useKeyPress } from 'ahooks'
import { EventEmitter } from 'ahooks/lib/useEventEmitter'
import { Tooltip } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { EmojiPicker, Mention } from '@/components'

import s from './post-comments.module.less'

interface PostCommentTextareaProps {
  content: string
  loading?: boolean
  placeholder: string
  extraActions?: React.ReactElement
  autoFocus?: boolean
  onSubmit: (content: string) => Promise<void>
  onEsc?: () => void
  onChangeContent$: EventEmitter<string | null>
}

export const PostCommentTextarea = ({
  content: c,
  loading,
  placeholder,
  autoFocus,
  extraActions,
  onEsc,
  onSubmit,
  onChangeContent$,
}: PostCommentTextareaProps) => {
  const [t] = useTranslation()

  const textareaRef = React.useRef<HTMLTextAreaElement>()
  const [content, setContent] = React.useState(c)

  useKeyPress('Escape', () => onEsc && onEsc(), {
    target: textareaRef.current,
  })

  onChangeContent$?.useSubscription((c: string | null) => {
    if (content.length === 0 && c != null) {
      setContent(c)
    }
    if (textareaRef.current) {
      textareaRef.current.focus()
    }
  })

  const onPressEnter = async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (content.trim().length === 0) {
      e.preventDefault()
      return
    }
    if (!e.shiftKey) {
      e.preventDefault()
      if (!loading) {
        await onSubmit(content)
        setContent('')
      }
    }
  }

  return (
    <div className={s.commentTextarea}>
      <Mention
        ref={textareaRef}
        value={content}
        rows={1}
        placeholder={placeholder}
        autoFocus={autoFocus}
        autoSize={{ minRows: 1, maxRows: 6 }}
        onValueChange={(content) => setContent(content)}
        onPressEnter={onPressEnter}
        onFocus={(e) => {
          // 将光标移到最后
          const val = e.target.value
          e.target.value = ''
          e.target.value = val
        }}
      />
      <div className={s.actions}>
        {extraActions}
        <EmojiPicker
          onClick={(emoji) => {
            const textarea = textareaRef.current!
            const { selectionStart, selectionEnd, value: s } = textarea
            const content = s.substring(0, selectionStart) + emoji + s.substring(selectionEnd, s.length)
            setContent(content)
            // textarea value 改变后光标被移到了末尾，将光标移回开始前的地方
            window.setTimeout(() => {
              textarea.selectionStart = selectionStart + emoji.length
              textarea.selectionEnd = textarea.selectionStart
            }, 0)
          }}
        />
        <Tooltip title={t('post.editor.publish')}>
          <div className={content ? s.publish : s.disableP}>
            {loading ? (
              <LoadingOutlined />
            ) : (
              <SendOutlined
                onClick={
                  content
                    ? async () => {
                        await onSubmit(content)
                        setContent('')
                      }
                    : undefined
                }
              >
                {t('post.editor.publish')}
              </SendOutlined>
            )}
          </div>
        </Tooltip>
      </div>
    </div>
  )
}
